import s from './RestConcerts.module.sass'

import { mappingPaymentItem, generateCalendarDates, generateRequestDate, filterDaysToSelect } from './helpers'

import React, { FC, useEffect, useState } from 'react'

import { ButtonLoadMore } from '@shared/ui/Actions/ButtonLoadMore'
import { Carousel } from '@shared/ui/Layout/Carousel'
import { ContainerLongLeft } from '@shared/ui/Layout/Containers/ContainerLongLeft'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { Title } from '@shared/ui/Typography/Title'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import CarouselSlider from '@shared/ui/Layout/Carousel/CarouselSlider'
import { EventCard } from '@widgets/Restaurant/RestConcerts/ui/EventCard'
import { CalendarSlider } from '@widgets/Restaurant/RestConcerts/ui/CalendarSlider'
import { LightGQL } from '@shared/api/gql-service/LightGQLService'
import { GQL_TAGS } from '@shared/api/api.gql'
import { CalendarSliderCellSkeleton } from '@widgets/Restaurant/RestConcerts/ui/CalendarSliderCell'

interface Props {
  restId: number | string
}

const NOW_DATE = new Date().getTime()
const TODAY_STARTING_DATE = new Date(NOW_DATE - (NOW_DATE % 86400000))
const DEFAULT_DAYS_LOAD_INTERVAL = 365

const mergeArrays = (...arrays) => [].concat(...arrays)

/**
 * @function RestConcerts — компонент с событиями Афиши в ресторанах
 */
const RestConcerts: FC<Props> = ({ restId }) => {
  const { isMobile, isTablet } = useClientResponsive()

  // TODO: need pagination for desktop; offset 8 -> default by design
  const cardsPerPage = isMobile ? 99 : 24
  const skeletonsTpl = !isTablet ? [0, 1, 2, 3] : [0, 1, 3]

  const [isSomePaymentExist, setIsSomePaymentExist] = useState(false)
  const [selectedDay, setSelectedDay] = useState(null)
  const [paymentItems, setPaymentItems] = useState(null)

  const [availableDays, setAvailableDays] = useState(null)
  const [calendarState, setCalendarState] = useState(null)

  const dateRangeFrom = generateRequestDate(selectedDay)
  const dateRangeTo = selectedDay
    ? generateRequestDate(TODAY_STARTING_DATE, selectedDay)
    : generateRequestDate(
        TODAY_STARTING_DATE,
        new Date().setDate(new Date(TODAY_STARTING_DATE).getDate() + DEFAULT_DAYS_LOAD_INTERVAL),
      )

  const calendarDatesArr = filterDaysToSelect(
    generateCalendarDates(TODAY_STARTING_DATE, DEFAULT_DAYS_LOAD_INTERVAL),
    availableDays,
  )

  const [cardsLoadOffset, setCardsLoadOffset] = useState(0)
  const [allTotalCards, setAllTotalCards] = useState(0)
  const [loadedCardsCount, setLoadedCardsCount] = useState(0)
  const [isShowMoreLoading, setIsShowMoreLoading] = useState(false)
  const [paymentItemsLoading, setPaymentItemsLoading] = useState(false)
  const needToShowMore = allTotalCards > loadedCardsCount

  // * 1. единичный запрос информации о наличии расписания в данном ресторане, для отображения календаря *
  const getScheduleGraphReq = () => {
    LightGQL.afisha
      .query(GQL_TAGS.CONCERT_CALENDAR, {
        variables: {
          offset: 0,
          first: 99,
          query: `dateRange=${dateRangeFrom}--${dateRangeTo}`,
          restId: `Restaurant_${restId}`,
        },
      })
      .then((response) => {
        const outputState =
          response?.place?.scheduleMeta?.any ||
          response?.place?.scheduleMeta?.concert ||
          response?.place?.scheduleMeta?.exhibition ||
          response?.place?.scheduleMeta?.movie ||
          response?.place?.scheduleMeta?.performance ||
          response?.place?.scheduleMeta?.sport

        setIsSomePaymentExist(outputState)
      })
      .catch((error) => console.error(`getScheduleGraphReq error message: ${error.message}`))
  }

  // * 2. единичный запрос на деактивацию ненужных дат в календаре *
  const getCalendarInfoGraphReq = () => {
    LightGQL.afisha
      .query(GQL_TAGS.CONCERT_CALENDAR_DAYS_AVAILABLE, {
        variables: {
          offset: 0,
          first: 99,
          query: `dateRange=${dateRangeFrom}--${dateRangeTo}`,
          restId: `Restaurant_${restId}`,
        },
      })
      .then((response) => {
        if (!selectedDay) {
          const resScheduleConcerts =
            response?.place?.scheduleConcerts?.items?.length > 0 && response.place.scheduleConcerts.items
          const resScheduleExhibitions =
            response?.place?.scheduleExhibitions?.items?.length > 0 && response.place.scheduleExhibitions.items
          const resScheduleMovies =
            response?.place?.scheduleMovies?.items?.length > 0 && response.place.scheduleMovies.items
          const resSchedulePerformances =
            response?.place?.schedulePerformances?.items?.length > 0 && response.place.schedulePerformances.items
          const resScheduleSports =
            response?.place?.scheduleSports?.items?.length > 0 && response.place.scheduleSports.items

          const resItems = mergeArrays(
            resScheduleConcerts,
            resScheduleExhibitions,
            resScheduleMovies,
            resSchedulePerformances,
            resScheduleSports,
          ).filter((item) => !!item)

          // TODO: get flattenUniqItems from resData / filters
          const flattenUniqItems =
            resItems?.length > 0
              ? resItems
                  .reduce((acc, item) => [...acc, ...item.sessions], [])
                  .map((item) => item?.dateTime?.split('T')[0])
                  .reduce((acc, cur) => (acc.indexOf(cur) === -1 ? acc.concat(cur) : acc), [])
              : null

          if (flattenUniqItems?.length > 0) {
            setAvailableDays(flattenUniqItems)
          } else {
            setCalendarState('error')
          }
        }
      })
      .catch((error) => console.error(`getCalendarInfoGraphReq error message: ${error.message}`))
  }

  // * 3. запрос событий по ресторану на конкретные дни с информацией о концерте и разных сессиях *
  const getPaymentGraphReq = () => {
    setPaymentItemsLoading(true)
    LightGQL.afisha
      .query(GQL_TAGS.CONCERT_PAYMENTS_LIST, {
        variables: {
          offset: cardsLoadOffset,
          first: cardsPerPage,
          query: `dateRange=${dateRangeFrom}--${dateRangeTo}`,
          restId: `Restaurant_${restId}`,
        },
      })
      .then((response) => {
        setLoadedCardsCount(cardsLoadOffset + cardsPerPage)

        const schedules = [
          'scheduleConcerts',
          'scheduleExhibitions',
          'scheduleMovies',
          'schedulePerformances',
          'scheduleSports',
        ]

        const extractedItems = schedules.flatMap((key) => response?.place?.[key]?.items || [])
        const hasResult = extractedItems.length > 0
        const resultItems = hasResult ? mappingPaymentItem(extractedItems) : null
        const newPaymentItems = paymentItems ? [...paymentItems, ...resultItems] : resultItems

        setPaymentItems(newPaymentItems)

        const totalCards = schedules.reduce((sum, key) => sum + (response?.place?.[key]?.totalCount || 0), 0)
        setAllTotalCards(totalCards)

        setIsShowMoreLoading(false)
      })
      .catch((error) => console.error(`getPaymentGraphReq error message: ${error.message}`))
      .finally(() => setPaymentItemsLoading(false))
  }

  useEffect(() => {
    getScheduleGraphReq()
    getCalendarInfoGraphReq()
  }, [restId])

  useEffect(() => {
    getPaymentsCallback()
  }, [JSON.stringify(selectedDay), cardsLoadOffset, restId])

  const hasPaymentItems = paymentItems && paymentItems.length > 0

  const hasButtonPrev = !isMobile
  const hasButtonNext = !isMobile
  const hasOverlayPrev = !isMobile
  const hasOverlayNext = true

  const handleDaySelection = (thisDay) => {
    if (!paymentItemsLoading) {
      setPaymentItems(null)
      setSelectedDay(thisDay.value)
      setCardsLoadOffset(0)
    }
  }

  const handleDeselect = () => {
    if (!paymentItemsLoading) {
      setPaymentItems(null)
      setSelectedDay(null)
      setCardsLoadOffset(0)
    }
  }

  const getPaymentsCallback = () => {
    const needToMakeRequest = !paymentItemsLoading
    if (needToMakeRequest) getPaymentGraphReq()
  }

  const handleShowMore = () => {
    setIsShowMoreLoading(true)
    setCardsLoadOffset(cardsLoadOffset + cardsPerPage)
  }

  return (
    <ErrorBoundary>
      {isSomePaymentExist && calendarState !== 'error' && (
        <section className={s['wrapper']}>
          <ContainerLongLeft>
            <Title tag='h2' sizes='h2 h2 h2 h4' className={s['title']}>
              Афиша событий
            </Title>

            <CalendarSlider
              hasButtonPrev={hasButtonPrev}
              hasButtonNext={hasButtonNext}
              hasOverlayPrev={hasOverlayPrev}
              hasOverlayNext={hasOverlayNext}
              daySelect={selectedDay}
              days={calendarDatesArr}
              handleDaySelection={handleDaySelection}
              handleDeselect={handleDeselect}
            />
          </ContainerLongLeft>

          <ContainerLongLeft>
            {hasPaymentItems ? (
              <>
                <Carousel>
                  <CarouselSlider
                    className={`${s['payment-list']}`}
                    spacingMobile={8}
                    spacingDesktop={0}
                    // @ts-ignore
                    countSlidesOnContainer={cardsPerPage}
                    disabled={!isMobile}>
                    {paymentItems.map((thisItem, index) => {
                      try {
                        return (
                          <EventCard
                            key={index}
                            data={thisItem}
                            analytic={{ section_index: index, object_id: thisItem.id }}
                          />
                        )
                      } catch (e) {
                        console.error(e)
                        return false
                      }
                    })}
                  </CarouselSlider>
                </Carousel>

                {!isMobile && needToShowMore && (
                  <div className={s['payment-show-more-wrap']}>
                    {/*TODO: Заменить на компонент Pagination*/}
                    <ButtonLoadMore
                      // @ts-ignore
                      onClick={handleShowMore}
                      className={s['payment-show-more-button']}
                      loading={isShowMoreLoading}
                      // @ts-ignore
                      nextPage={needToShowMore}
                      analytic={{ section_name: 'События/концерты в ресторане' }}
                    />
                  </div>
                )}
              </>
            ) : (
              <ul className={`${s['payment-list']} ${s['payment-list--skeleton']}`}>
                {skeletonsTpl.map((skeleton, index) => (
                  <CalendarSliderCellSkeleton sizes={'L ML'} key={`skeleton-${index}`} />
                ))}
              </ul>
            )}
          </ContainerLongLeft>
        </section>
      )}
    </ErrorBoundary>
  )
}

export default RestConcerts
